import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {Timeline, TimelineLite
} from "gsap";
import {SpacierArcadeParsedGameData} from "../../server/data/SpacierArcadeParsedGameData";
import {ParsedGameData} from "@nolimitcity/slot-game/bin/core/server/data/ParsedGameData";
import {IBetWayWinData, IBetWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {SpacierArcadeGameConfig} from "../../SpacierArcadeGameConfig";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {SpacierArcadeGameModuleConfig} from "../../SpacierArcadeGameModuleConfig";
import {ISymbolStateConfig, StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {SASymbols, SpacierArcadeReelSymbol} from "../../symbols/SpacierArcadeReelSymbol";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TweenLite, Linear, RoughEase} from "gsap";
import {SpacierArcadeGameMode} from "../../gamemode/SpacierArcadeGameMode";


export class SAInitialWinPresentation {
    private _symbolWinStates:boolean[][];
    private _tl:TimelineLite;

    constructor() {
    }

    public prepare(data:SpacierArcadeParsedGameData):void {
        this._symbolWinStates = ArrayHelper.initArrayWithValues<boolean[]>(GameConfig.instance.REELS_NUM, () => {
            return ArrayHelper.initArrayWithValues<boolean>(SpacierArcadeGameConfig.instance.SYMBOLS_NUM_IN_REEL[2], () => false)
        });

        for (let l: number = 0; l < data.betWins.length; l++) {
            const betWayWinData: IBetWayWinData = <IBetWayWinData>data.betWins[l];
            for (let i: number = 0; i < betWayWinData.reelWinPositions.length; i++) {
                for (let j: number = 0; j < betWayWinData.reelWinPositions[i].length; j++) {
                    if (betWayWinData.reelWinPositions[i][j]) {
                        this._symbolWinStates[i][j] = true;
                    }
                }
            }
        }
    }

    public getAnimation(data:SpacierArcadeParsedGameData):TimelineLite | null {
        if(data.singleWin <= 0) {
            return null;
        }
        this.prepare(data);
        const tl:TimelineLite = new TimelineLite();
        const noWinAnimations:TimelineLite = this.getNoWinSymbolAnimations(data);
        const winAnimations:TimelineLite = this.getWinSymbolAnimations(data);

        tl.add([
            noWinAnimations,
            winAnimations,
        ], 0);

        if(data.currentWinRatioConfig!.ratio >= GameConfig.instance.WIN_RATIOS.normalWin[3].ratio){
            tl.add(TweenLite.fromTo(StageManager.stage, 0.4, {x:-0.5, y: -0.5}, {x:1, y:1, ease:new RoughEase({strength:8, points:20, template:Linear.easeNone, randomize:false}) , clearProps:"x"}), 0.6);
        }

        return tl;
    }

    private isNextFreeSpin(data:SpacierArcadeParsedGameData):boolean{
        return data.mode === SpacierArcadeGameMode.NORMAL && data.nextMode === SpacierArcadeGameMode.FREESPIN;

    }

    protected addWinCountUpToTimeline(data:SpacierArcadeParsedGameData): TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        const totalWin: number = data.totalWin;
        const from: number = totalWin - data.singleWin;
        const duration: number = data.currentWinRatioConfig!.duration;

        let countUpTimeline: TimelineLite = new TimelineLite();
        countUpTimeline.add(SpacierArcadeGameModuleConfig.saWinCountUp.getCountUpAnimation(data.totalWinWaysWon, 0, data.singleWin, duration, data.currentMultiplier, data.currentTotalWinRatioConfig));
        if(data.payoutMultiplier > 1) {
            countUpTimeline.add([
                SpacierArcadeGameModuleConfig.spacierMultiplierAddupController.animateMultipliers(),
                // this._winCountUp.getMultiShakeAnimation()
            ]);
            countUpTimeline.add(SpacierArcadeGameModuleConfig.saWinCountUp.getMultiplierCountUpAnimation(data.singleWin, data.totalWin, 0.01, data.payoutMultiplier, data.currentTotalWinRatioConfig, false), countUpTimeline.totalDuration() - 0.5);
        }
        // countUpTimeline.add(WinPresentationManager.getWinFieldCountUpAnimation(from, totalWin, {duration: 0}));
        tl.add(countUpTimeline);
        return tl;
    }


    public getNoWinSymbolAnimations(data:SpacierArcadeParsedGameData):TimelineLite {
        const tl:TimelineLite = new TimelineLite();

        for (let i: number = 0; i < this._symbolWinStates.length; i++) {
            for (let j: number = 0; j < this._symbolWinStates[i].length; j++) {
                if (this._symbolWinStates[i][j]) {
                    continue;
                }

                let noWinSymbol: StateReelSymbol | null = Reels.getSymbol(i, j);
                if (noWinSymbol === null || (this.isNextFreeSpin(data) && noWinSymbol!.symName === SASymbols.S)) {
                    continue;
                }

                tl.add(noWinSymbol!.changeState({
                    state: GameConfig.instance.SYMBOL_STATES.noWin!,
                    fadeInDuration: 0
                }), 0);
            }
        }

        return tl;
    }


    public getWinSymbolAnimations(data:SpacierArcadeParsedGameData):TimelineLite {
        let startTime:number = 0;
        const tl:TimelineLite = new TimelineLite();
        for(let k:number = 0; k < data.betWins.length; k++) {
            let betWayWinData: any = <IBetWayWinData>data.betWins[k];
            if(betWayWinData.symbol === SASymbols.S){
                for(let i:number = 1; i <= 3; i++) {
                    this.addWinAnimation(data, betWayWinData, tl, i);
                }
            }
            else {
                for (let i: number = 0; i < betWayWinData.length; i++) {
                    this.addWinAnimation(data, betWayWinData, tl, i);
                }
            }
        }

        return tl;
    }

    private addWinAnimation(data:SpacierArcadeParsedGameData, betWayWinData:any, tl:TimelineLite, i:number):void{
        for(let j:number = 0; j < betWayWinData.reelWinPositions[i].length; j++) {
            if(betWayWinData.reelWinPositions[i][j]) {
                let symbol: SpacierArcadeReelSymbol | null;
                symbol = <SpacierArcadeReelSymbol>Reels.getSymbol(i, j);
                symbol.multiplier = data.overlayWildsMultipliers[i][j];
                if(!symbol) {
                    debugger;
                    throw new Error(`Error: SequentiallyBetWayInitialWPController.playBetWinPresentation(): Cannot find symbol at reel ${i} symbol ${j}`);
                }
                tl.add(this.addSymbolWinAnimationToTimeline(symbol, data), 0);
            }
        }
    }

    // Returns the offset time in the main timeline from this symbol animation start to when the next symbol animation should start.
    protected addSymbolWinAnimationToTimeline(symbol:StateReelSymbol, data:SpacierArcadeParsedGameData):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        let state:ISymbolStateConfig = SpacierArcadeGameConfig.instance.SYMBOL_STATES.tinyWin;
        if(data.currentWinRatioConfig!.type === WinRatioType.SMALL || data.currentWinRatioConfig!.type === WinRatioType.MEDIUM){
            state = SpacierArcadeGameConfig.instance.SYMBOL_STATES.smallWin;
        }
        if (data.isBigWin || data.currentWinRatioConfig!.type === WinRatioType.LARGE) {
            state = GameConfig.instance.SYMBOL_STATES.win
        }

        tl.add([
            () => this.sortSymbolDepthToTop(symbol),
            symbol.changeState({
                state : state
            })
        ]);

        return tl;
    }


    protected sortSymbolDepthToTop(symbol:StateReelSymbol):void {
        if(symbol && symbol.parent) {
            symbol.parent.addChild(symbol);
        }
    }
}